import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { SpinResult, WheelState } from '../lib/types';
import { WheelSvg, targetRotation } from '../components/WheelSvg';
import { Sheet } from '../components/Sheet';
import { Countdown } from '../components/Countdown';
import { CoinIcon, CrystalIcon, TicketIcon } from '../components/Currency';
import { nfmt, plural } from '../lib/format';
import { haptic, tg } from '../lib/telegram';
import { useApp } from '../App';

const SPIN_MS = 4600;

export function WheelScreen() {
  const { refresh, toast, goPrizes, goTab } = useApp();
  const [state, setState] = useState<WheelState | null>(null);
  const [rotation, setRotation] = useState(0);
  const [duration, setDuration] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<SpinResult | null>(null);
  const [noSpins, setNoSpins] = useState<{ nextFreeSpinAt: string | null } | null>(null);
  const [refLink, setRefLink] = useState('');
  const pending = useRef<SpinResult | null>(null);

  useEffect(() => {
    api.get<WheelState>('/wheel').then(setState).catch(() => {});
    api.get<{ link: string }>('/referrals').then((r) => setRefLink(r.link)).catch(() => {});
  }, []);

  async function spin() {
    if (!state || spinning) return;
    haptic.tap();
    setSpinning(true);
    try {
      const res = await api.post<SpinResult>('/wheel/spin');
      const index = state.sectors.findIndex((s) => s.id === res.sector.id);
      pending.current = res;
      setDuration(SPIN_MS);
      setRotation((r) => targetRotation(r, Math.max(index, 0), state.sectors.length));
      setState({ ...state, spinsLeft: res.spinsLeft });
    } catch (e) {
      setSpinning(false);
      const err = e as ApiError;
      if (err.code === 'no_spins') {
        haptic.no();
        setNoSpins({ nextFreeSpinAt: (err.data?.nextFreeSpinAt as string) ?? state.nextFreeSpinAt });
      } else {
        toast(err.message, 'bad');
      }
    }
  }

  function settled() {
    if (!pending.current) return;
    haptic.ok();
    setResult(pending.current);
    pending.current = null;
    setSpinning(false);
    void refresh();
  }

  async function claimFree() {
    try {
      const r = await api.post<{ granted: boolean; nextAt: string }>('/wheel/free-spin');
      if (r.granted) {
        haptic.ok();
        setNoSpins(null);
        toast('Прокрут начислен');
        const fresh = await api.get<WheelState>('/wheel');
        setState(fresh);
        void refresh();
      } else {
        toast('Ещё рано — таймер на экране', 'bad');
      }
    } catch (e) {
      toast((e as ApiError).message, 'bad');
    }
  }

  function share() {
    haptic.tap();
    const text = 'Собираю коины на розыгрыш. Заходи, тебе тоже дадут прокруты.';
    tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(text)}`);
  }

  async function copy() {
    haptic.tap();
    try {
      await navigator.clipboard.writeText(refLink);
      toast('Ссылка скопирована');
    } catch {
      toast('Не вышло скопировать — выдели ссылку вручную', 'bad');
    }
  }

  if (!state) return <div className="h-64 animate-pulse rounded-3xl bg-surface" />;

  return (
    <div className="rise">
      <p className="mb-4 text-[15px] leading-relaxed text-ash">
        От гайдов до главного приза. Каждый прокрут что-то даёт — пустых секторов нет.
      </p>

      <button onClick={goPrizes}
        className="mb-5 flex w-full items-center justify-between rounded-2xl border border-line bg-surface px-4 py-3">
        <span className="text-sm font-semibold">Список призов</span>
        <span className="text-ash">→</span>
      </button>

      <div className="relative">
        <WheelSvg sectors={state.sectors} rotation={rotation} durationMs={duration} onSettled={settled} />
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <TicketIcon size={22} />
          <span className="mt-1 font-display text-3xl leading-none num">{state.spinsLeft}</span>
          <span className="mt-1 text-[10px] uppercase tracking-[.14em] text-ash">
            {plural(state.spinsLeft, 'прокрут', 'прокрута', 'прокрутов')}
          </span>
        </div>
      </div>

      <button className="btn-primary mt-5 w-full text-base" onClick={spin} disabled={spinning}>
        {spinning ? 'Крутится…' : 'Крутить колесо'}
      </button>
      <p className="mt-2 text-center text-xs text-ash">1 прокрут = 1 попытка</p>

      <section className="mt-8 rounded-2xl border border-turq/25 bg-turq/[.06] p-4">
        <h2 className="font-display text-[15px]">Зови друзей</h2>
        <p className="mt-1 text-sm leading-relaxed text-ash">
          За каждого, кто выполнит первое задание, — 100 коинов и 3 прокрута. Плюс 10% от всего,
          что он заработает дальше.
        </p>
        <div className="mt-3 flex gap-2">
          <button className="btn-primary flex-1" onClick={share}>Отправить</button>
          <button className="btn-ghost flex-1" onClick={copy}>Скопировать</button>
        </div>
      </section>

      {/* Прокруты закончились — три выхода, а не тупик (раздел 4.2) */}
      <Sheet open={!!noSpins} onClose={() => setNoSpins(null)} title="Прокруты закончились">
        <p className="mb-4 text-sm text-ash">Три способа получить ещё.</p>
        <div className="space-y-2">
          <Way title="Позови друга" note="+3 прокрута за каждого" action="Отправить" onClick={share} />
          <Way title="Выполни задание" note="Прокруты дают почти все задания" action="Открыть"
               onClick={() => { setNoSpins(null); goTab('tasks'); }} />
          <div className="flex items-center justify-between rounded-xl border border-line bg-raised px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Подожди сутки</p>
              <p className="text-xs text-ash">
                Бесплатный прокрут через <Countdown to={noSpins?.nextFreeSpinAt} className="text-chalk" />
              </p>
            </div>
            <button className="btn-ghost px-3 py-2 text-sm" onClick={claimFree}>Забрать</button>
          </div>
        </div>
      </Sheet>

      {/* Результат прокрута */}
      <Sheet open={!!result} onClose={() => setResult(null)}>
        {result && (
          <div className="pop text-center">
            <p className="label mb-3">Выпало</p>
            <div className="mb-4 flex justify-center">
              {result.sector.prize_type === 'crystals' ? <CrystalIcon size={56} />
                : result.sector.prize_type === 'spin' ? <TicketIcon size={56} />
                : <CoinIcon size={56} />}
            </div>
            <h2 className="font-display text-2xl">{result.sector.title}</h2>
            <p className="mt-2 text-sm text-ash">
              {result.awarded.coins > 0 && `+${nfmt(result.awarded.coins)} коинов`}
              {result.awarded.crystals > 0 && `+${nfmt(result.awarded.crystals)} кристаллов`}
              {result.awarded.spins > 0 && `+${result.awarded.spins} прокрут`}
              {result.awarded.file && 'Файл лежит в профиле'}
            </p>
            <button className="btn-primary mt-5 w-full"
              onClick={() => { setResult(null); if (result.spinsLeft > 0) void spin(); }}>
              {result.spinsLeft > 0 ? `Крутить ещё (${result.spinsLeft})` : 'Закрыть'}
            </button>
          </div>
        )}
      </Sheet>
    </div>
  );
}

function Way({ title, note, action, onClick }: {
  title: string; note: string; action: string; onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-line bg-raised px-4 py-3">
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-ash">{note}</p>
      </div>
      <button className="btn-ghost px-3 py-2 text-sm" onClick={onClick}>{action}</button>
    </div>
  );
}
